<div
  class="visits"
  [class.visits--has-selection]="selected() != null"
  [class.visits--landing]="mobileLanding()">
  <header class="visits__brand-bar">
    <img src="/scryber-logo-lime.png?v=4" alt="Scryber" />
    @if (selected(); as visit) {
      @if (canResumeVisit(visit)) {
        <button type="button" class="visits__resume visits__resume--bar" (click)="onResume(visit)">
          Resume
        </button>
      }
    }
  </header>
  <aside class="visits__list-pane">
    <div class="visits__list-toolbar">
      <button type="button" class="visits__new" (click)="onNewVisit()">+ New Visit</button>
      <div class="visits__list-tools">
        <label class="visits__active">
          <input type="checkbox" [ngModel]="activeOnly()" (ngModelChange)="activeOnly.set($event)" />
          Active visits
        </label>
        <button
          type="button"
          class="visits__icon-btn"
          aria-label="Search"
          (click)="searchOpen.set(!searchOpen())">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
        </button>
      </div>
      @if (searchOpen()) {
        <input
          class="visits__search"
          type="search"
          placeholder="Search visits"
          [ngModel]="searchText()"
          (ngModelChange)="searchText.set($event)" />
      }
    </div>

    @if (uploadError()) {
      <div class="visits__upload-error" role="alert">
        <p>{{ uploadError() }}</p>
        <button type="button" class="visits__upload-error-dismiss" (click)="dismissUploadError()">
          Dismiss
        </button>
      </div>
    }

    @if (loading()) {
      <p class="visits__muted">Loading visits…</p>
    } @else if (error()) {
      <p class="visits__error">{{ error() }}</p>
    } @else if (!groups().length) {
      <div class="visits__empty">
        <div class="visits__drafting-brand" aria-hidden="true">
          <div class="visits__drafting-logo-wrap">
            <img class="visits__drafting-logo visits__empty-logo" src="/scryber-logo-lime.png?v=4" alt="" />
          </div>
        </div>
        <p class="visits__empty-copy">No visits yet. Click New Visit to get started</p>
      </div>
    } @else {
      <div class="visits__groups">
        @for (group of groups(); track group.label) {
          <div class="visits__group">
            <h2 class="visits__group-label">{{ group.label }}</h2>
            <div class="visits__group-cards">
              @for (visit of group.visits; track visit.id) {
                <article
                  class="visits__card"
                  [class.visits__card--selected]="isSelected(visit)"
                  [class.visits__card--paused]="cardStatus(visit) === 'Paused'"
                  [class.visits__card--unread]="visit.soapUnread === true"
                  [class.visits__card--pause-badge]="cardStatus(visit) === 'Paused'"
                  role="button"
                  tabindex="0"
                  [attr.aria-current]="isSelected(visit) ? 'true' : null"
                  (click)="onVisitCardClick(visit)"
                  (keydown)="onVisitCardKeydown($event, visit)">
                  @if (cardStatus(visit) === 'Paused') {
                    <span class="visits__card-pause" aria-hidden="true" title="Paused">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                        <rect x="6" y="5" width="4" height="14" rx="1" />
                        <rect x="14" y="5" width="4" height="14" rx="1" />
                      </svg>
                    </span>
                  }
                  <div class="visits__card-title">{{ patientLabel(visit) }}</div>
                  <div class="visits__card-meta">{{ metaLine(visit) }}</div>
                  <div class="visits__card-status" [ngClass]="statusClass(visit.status, visit)">
                    @if (cardStatus(visit) === 'Completed') {
                      ✓ Completed
                    } @else if (isPipelineStatus(cardStatus(visit))) {
                      ● {{ cardStatus(visit) }}
                    } @else {
                      {{ cardStatus(visit) }}
                    }
                  </div>
                </article>
              }
            </div>
          </div>
        }
      </div>
    }
  </aside>

  <section class="visits__detail-pane">
    @if (!selected()) {
      <div class="visits__landing">
        <button
          type="button"
          class="visits__back"
          aria-label="Back to visits"
          (click)="onLandingBack()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M15 18 9 12l6-6" />
          </svg>
          Back
        </button>
        <h1 class="visits__landing-title">Select a Patient to start recording</h1>
        <div class="visits__landing-search">
          <input
            #patientSearchInput
            class="visits__landing-input"
            type="text"
            autocomplete="off"
            placeholder="Select or add patient"
            [ngModel]="patientQuery()"
            (ngModelChange)="onPatientQueryChange($event)"
            (focus)="onPatientSearchFocus()"
            (keydown)="onPatientSearchKeydown($event)"
            aria-label="Select or add patient"
            aria-autocomplete="list"
            [attr.aria-expanded]="patientDropdownOpen()" />

          @if (patientDropdownOpen() && (landingSuggestions().length || showAddPatientOption())) {
            <div class="visits__landing-dropdown" role="listbox">
              @for (patient of landingSuggestions(); track patient.id; let i = $index) {
                <button
                  type="button"
                  class="visits__landing-option"
                  role="option"
                  [class.visits__landing-option--active]="patientHighlight() === i"
                  (mousedown)="$event.preventDefault()"
                  (click)="selectLandingPatient(patient)"
                  (mouseenter)="patientHighlight.set(i)">
                  <span class="visits__landing-option-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                  <span class="visits__landing-option-text">
                    <span class="visits__landing-option-name">
                      {{ patient.firstName }} {{ patient.lastName }}
                    </span>
                    <span class="visits__landing-option-meta">{{ patient.lastVisitLabel }}</span>
                  </span>
                </button>
              }
              @if (showAddPatientOption()) {
                <button
                  type="button"
                  class="visits__landing-option visits__landing-option--add"
                  role="option"
                  [disabled]="patientCreating()"
                  [class.visits__landing-option--active]="patientHighlight() === landingSuggestions().length"
                  (mousedown)="$event.preventDefault()"
                  (click)="addNewPatientFromQuery()"
                  (mouseenter)="patientHighlight.set(landingSuggestions().length)">
                  <span class="visits__landing-option-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </span>
                  <span class="visits__landing-option-text">
                    <span class="visits__landing-option-name">
                      @if (patientCreating()) {
                        Creating '{{ patientQuery().trim() }}'…
                      } @else {
                        Add '{{ patientQuery().trim() }}'
                      }
                    </span>
                  </span>
                </button>
              }
            </div>
          }
          <button
            type="button"
            class="visits__landing-skip"
            [disabled]="patientCreating()"
            (click)="continueWithoutPatient()">
            @if (patientCreating() && !patientQuery().trim()) {
              Starting…
            } @else {
              Skip patient selection
            }
          </button>
        </div>
        @if (patientCreateError()) {
          <p class="visits__landing-error" role="alert">{{ patientCreateError() }}</p>
        }
      </div>
    } @else {
      <header class="visits__detail-header">
        <div class="visits__detail-heading">
          @if (isEditingPatientName(selected()!)) {
            <div class="visits__detail-name-edit">
              <div class="visits__detail-name-edit-row">
                <div class="visits__detail-name-edit-field">
                  <input
                    #patientNameEditInput
                    class="visits__detail-name-input"
                    type="text"
                    autocomplete="off"
                    placeholder="Patient name"
                    [ngModel]="editingPatientName()"
                    (ngModelChange)="onEditingPatientNameChange($event)"
                    (keydown)="onNameEditKeydown($event, selected()!)"
                    [disabled]="isSavingPatientName(selected()!)"
                    aria-label="Edit patient name"
                    aria-autocomplete="list"
                    [attr.aria-expanded]="nameEditDropdownOpen()" />
                  @if (nameEditDropdownOpen() && nameEditSuggestions().length) {
                    <div class="visits__detail-name-dropdown" role="listbox">
                      @for (patient of nameEditSuggestions(); track patient.id; let i = $index) {
                        <button
                          type="button"
                          class="visits__landing-option"
                          role="option"
                          [class.visits__landing-option--active]="nameEditHighlight() === i"
                          (mousedown)="$event.preventDefault()"
                          (click)="selectNameEditPatient(patient, selected()!)"
                          (mouseenter)="nameEditHighlight.set(i)">
                          <span class="visits__landing-option-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                          </span>
                          <span class="visits__landing-option-text">
                            <span class="visits__landing-option-name">
                              {{ patient.firstName }} {{ patient.lastName }}
                            </span>
                            <span class="visits__landing-option-meta">{{ patient.lastVisitLabel }}</span>
                          </span>
                        </button>
                      }
                    </div>
                  }
                </div>
                <div class="visits__detail-name-edit-actions">
                  <button
                    type="button"
                    class="visits__detail-name-save"
                    title="Save"
                    aria-label="Save patient name"
                    [disabled]="isSavingPatientName(selected()!)"
                    (click)="savePatientNameEdit(selected()!, $event)">
                    @if (isSavingPatientName(selected()!)) {
                      …
                    } @else {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    }
                  </button>
                  <button
                    type="button"
                    class="visits__detail-name-cancel"
                    title="Cancel"
                    aria-label="Cancel editing patient name"
                    [disabled]="isSavingPatientName(selected()!)"
                    (click)="cancelPatientNameEdit($event)">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              @if (nameEditError()) {
                <p class="visits__detail-name-edit-error" role="alert">{{ nameEditError() }}</p>
              }
            </div>
          } @else {
            <div class="visits__detail-title">
              <h1>{{ patientLabel(selected()!) }}</h1>
              @if (canEditPatientName(selected()!)) {
                <button
                  type="button"
                  class="visits__detail-edit-name"
                  [class.visits__detail-edit-name--unknown]="isUnknownPatient(selected()!)"
                  title="Edit patient name"
                  aria-label="Edit patient name"
                  (click)="startPatientNameEdit(selected()!, $event)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                </button>
              }
            </div>
          }
          <p class="visits__detail-sub">
            <span class="visits__detail-meta">
              @if (showTranscriptLink(selected()!)) {
                <button
                  type="button"
                  class="visits__transcript-link"
                  [class.visits__transcript-link--active]="detailMode() === 'transcript'"
                  (click)="onTranscriptionClick(selected()!, $event)">
                  Transcription
                </button>
                <span> · </span>
              }
              <span>{{ detailWhenLine(selected()!) }}</span>
              @if (cardStatus(selected()!) === 'Completed') {
                <span class="visits__check">✓</span>
              }
            </span>
            @if (canCopyAllNotes()) {
              <button
                type="button"
                class="visits__copy-all"
                aria-label="Copy All notes"
                (click)="copyAllNotes()">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy All
              </button>
            }
          </p>
        </div>
        <button
          type="button"
          class="visits__back"
          aria-label="Back to visits"
          (click)="onMobileBack()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M15 18 9 12l6-6" />
          </svg>
          Back
        </button>
        <div class="visits__detail-actions">
          @if (canResumeVisit(selected()!)) {
            <button type="button" class="visits__resume" (click)="onResume(selected()!)">
              Resume
            </button>
          }
        </div>
      </header>

      @if (detailMode() === 'transcript') {
        <div class="visits__transcript">
          <div class="visits__transcript-head">
            <div class="visits__transcript-title-row">
              <span class="visits__transcript-doc" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6M8 13h8M8 17h8M8 9h2" />
                </svg>
              </span>
              <h2>Transcription Content</h2>
            </div>
            <p class="visits__transcript-sub">Full transcription of the consultation</p>
          </div>

          <div
            class="visits__transcript-toolbar"
            [class.visits__transcript-toolbar--end]="!hasTranslatedTranscript()">
            @if (hasTranslatedTranscript()) {
              <div class="visits__transcript-tabs">
                <button
                  type="button"
                  class="visits__transcript-tab"
                  [class.visits__transcript-tab--active]="transcriptTab() === 'translated'"
                  (click)="onTranscriptTabChange('translated')">
                  Translated (English)
                </button>
                <button
                  type="button"
                  class="visits__transcript-tab"
                  [class.visits__transcript-tab--active]="transcriptTab() === 'original'"
                  (click)="onTranscriptTabChange('original')">
                  Original
                </button>
              </div>
            }
            <button
              type="button"
              class="visits__transcript-copy"
              aria-label="Copy transcript"
              (click)="copyDisplayedTranscript()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </button>
          </div>

          <div class="visits__transcript-body">
            @if (transcriptLoading()) {
              <p class="visits__muted">Loading transcript…</p>
            } @else if (displayedTranscript(); as transcript) {
              <div class="visits__transcript-text" [innerHTML]="formatTranscriptHtml(transcript)"></div>
            } @else {
              <p class="visits__muted">No transcript content available.</p>
            }
          </div>
        </div>
      } @else if (draftingNote() || pausedNote()) {
        <div
          class="visits__drafting"
          [class.visits__drafting--paused]="pausedNote()"
          aria-live="polite">
          <div class="visits__drafting-brand" aria-hidden="true">
            <div class="visits__drafting-logo-wrap">
              <img class="visits__drafting-logo" src="/scryber-logo-lime.png?v=4" alt="" />
            </div>
            @if (pausedNote()) {
              <div class="visits__drafting-pause" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                  <rect x="6" y="5" width="4" height="14" rx="1" />
                  <rect x="14" y="5" width="4" height="14" rx="1" />
                </svg>
              </div>
            } @else {
              <div class="visits__drafting-wave">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>
            }
          </div>
          @if (pausedNote()) {
            <h2 class="visits__drafting-title">Transcription paused</h2>
            <p class="visits__drafting-sub">
              Your note will be available when this visit is ended.
            </p>
          } @else {
            <h2 class="visits__drafting-title">Building transcription notes</h2>
            <p class="visits__drafting-sub">Your note will appear here as soon as it is ready.</p>
          }
        </div>
      } @else if (detailLoading()) {
        <p class="visits__muted">Loading notes…</p>
      } @else {
        <div class="visits__note-panel">
          <div class="visits__sections">
          @for (section of sections(); track section.id) {
            <article class="visits__section">
              <div class="visits__section-head">
                <button type="button" class="visits__collapse" (click)="toggleSection(section.id)" [attr.aria-expanded]="!section.collapsed">
                  {{ section.collapsed ? '▶' : '▼' }}
                </button>
                <h2>{{ section.title }}</h2>
                @if (isEditableSection(section.id)) {
                  <button
                    type="button"
                    class="visits__edit"
                    [class.visits__edit--done]="editingSectionId() === section.id"
                    [disabled]="isSavingNotes()"
                    [attr.aria-label]="editingSectionId() === section.id ? 'Done editing' : 'Edit section'"
                    [title]="editingSectionId() === section.id ? 'Done' : 'Edit'"
                    (click)="toggleSectionEdit(section)">
                    @if (editingSectionId() === section.id) {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    } @else {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                      </svg>
                    }
                  </button>
                }
                <button
                  type="button"
                  class="visits__copy"
                  aria-label="Copy section"
                  (click)="copySection(section.body)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
              @if (!section.collapsed) {
                @if (editingSectionId() === section.id) {
                  <textarea
                    #soapTextarea
                    class="visits__section-textarea"
                    rows="6"
                    [value]="section.body"
                    (input)="onSectionBodyInput(section.id, $event)"></textarea>
                } @else if (section.body) {
                  @if (section.id === 'icd-10') {
                    <div class="visits__section-body visits__section-body--codes">{{ section.body }}</div>
                  } @else {
                    <div
                      class="visits__section-body"
                      [class.visits__section-body--objective]="section.id === 'objective'"
                      [class.visits__section-body--hpi]="section.id === 'hpi'"
                      [class.visits__section-body--ap]="section.id === 'assessment-plan'"
                      [class.visits__section-body--pi]="section.id === 'patient-instructions'"
                      [class.visits__section-body--tps]="section.id === 'treatment-plan-summary'"
                      [innerHTML]="section.body | soapToHtml:soapHtmlContext(section)"></div>
                  }
                } @else {
                  <div class="visits__section-body visits__section-body--empty">—</div>
                }
              }
            </article>
          }
          @if (reanalyzeError(); as message) {
            <p class="visits__reanalyze-error" role="alert">{{ message }}</p>
          }
          @if (noteSaveError(); as message) {
            <p class="visits__reanalyze-error" role="alert">{{ message }}</p>
          }
          </div>
          <footer class="visits__note-bar">
            <div class="visits__note-bar-icons">
              <button
                type="button"
                class="visits__note-bar-btn"
                [class.visits__note-bar-btn--active]="noteFeedback() === 'up'"
                aria-label="Like note"
                (click)="setNoteFeedback('up')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
                  <path d="M7 10v11H4a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1h3zm0 0V8a4 4 0 0 1 4-4h.5c.8 0 1.5.5 1.8 1.2L15 10h4.2a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 18.1 21H11a4 4 0 0 1-4-4" />
                </svg>
              </button>
              <button
                type="button"
                class="visits__note-bar-btn"
                [class.visits__note-bar-btn--active]="noteFeedback() === 'down'"
                aria-label="Dislike note"
                (click)="setNoteFeedback('down')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
                  <path d="M17 14V3h3a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-3zm0 0v2a4 4 0 0 1-4 4h-.5a2 2 0 0 1-1.8-1.2L9 14H4.8a2 2 0 0 1-2-2.3l1.2-7A2 2 0 0 1 5.9 3H13a4 4 0 0 1 4 4" />
                </svg>
              </button>
              @if (selected(); as visit) {
                @if (canDeleteVisit(visit)) {
                  <button
                    type="button"
                    class="visits__note-bar-btn visits__note-bar-btn--delete"
                    title="Delete visit"
                    aria-label="Delete visit"
                    [disabled]="isDeletingVisit(visit)"
                    (click)="onDeleteVisit(visit, $event)">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
                      <path d="M3 6h18" />
                      <path d="M8 6V4h8v2" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      <path d="M10 11v6M14 11v6" />
                    </svg>
                  </button>
                }
              }
            </div>
            <div class="visits__note-bar-actions">
              <button
                type="button"
                class="visits__reset"
                [disabled]="!hasNoteChanges() || isSavingNotes()"
                (click)="onResetNotes()">
                Reset
              </button>
              <button
                type="button"
                class="visits__save"
                [disabled]="!hasNoteChanges() || isSavingNotes()"
                (click)="onSaveNotes()">
                {{ isSavingNotes() ? 'Saving…' : 'Save' }}
              </button>
              <button
                type="button"
                class="visits__reanalyze"
                [disabled]="isReanalyzing() || isSavingNotes()"
                (click)="onReanalyze()">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                  [class.visits__reanalyze-icon--spin]="isReanalyzing()">
                  <path d="M21 12a9 9 0 1 1-2.6-6.4" />
                  <path d="M21 3v6h-6" />
                </svg>
                {{ isReanalyzing() ? 'Re-analyzing…' : 'Re-analyze' }}
              </button>
            </div>
          </footer>
        </div>
      }
    }
  </section>

  @if (deleteConfirmVisible()) {
    <div class="visits__modal-backdrop" (click)="cancelDeleteConfirm()">
      <div
        class="visits__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="visits-delete-title"
        (click)="$event.stopPropagation()">
        <h2 id="visits-delete-title" class="visits__modal-title">Delete visit?</h2>
        <p class="visits__modal-copy">
          Delete visit for <strong>{{ deleteConfirmPatientLabel() }}</strong>? This cannot be undone.
        </p>
        @if (deleteError()) {
          <p class="visits__modal-error" role="alert">{{ deleteError() }}</p>
        }
        <div class="visits__modal-actions">
          <button
            type="button"
            class="visits__modal-cancel"
            [disabled]="!!deletingVisitId()"
            (click)="cancelDeleteConfirm()">
            Cancel
          </button>
          <button
            type="button"
            class="visits__modal-delete"
            [disabled]="!pendingDeleteVisit() || !!deletingVisitId()"
            (click)="confirmDeleteVisit()">
            {{ deletingVisitId() ? 'Deleting…' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  }
</div>
